import { FC, ReactNode, useMemo } from "react"
import { Check } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { useAppSelector } from "../../../../hooks"
import { selectUserDetails } from "../../../../../redux/slices/auth"
import {
  useFetchLoanOfficerQuery,
  useFetchOrgDetailsQuery
} from "../../../../../redux/queries"
import { getUserBrandId } from "../../../../utils"
import { Integration } from "../../../../../types"
import LeadWorkflowSelector from "../lead-workflow-selector"
import ContactFormSelector from "../contact-form-selector"
import RateCardsSelector from "../rate-cards-selector"
import RateTableSelector from "../rate-table-selector"

interface ModuleTileProps {
  id: string
  label: string
  desc: string
  checked: boolean
  onChange: () => void
  subSelector?: ReactNode
}

const ModuleTile: FC<ModuleTileProps> = ({
  label,
  desc,
  checked,
  onChange,
  subSelector
}) => (
  <div
    className={[
      "rounded-lg border bg-white transition-all",
      checked
        ? "border-primary-500"
        : "border-neutral-200 hover:border-neutral-300"
    ].join(" ")}
  >
    <div
      onClick={onChange}
      className="flex items-start gap-5 p-5 cursor-pointer select-none"
    >
      <div
        className={[
          "flex-shrink-0 mt-0.5 rounded-[3px] border flex items-center justify-center transition-all",
          checked
            ? "bg-primary-500 border-primary-500"
            : "bg-white border-neutral-400"
        ].join(" ")}
        style={{ width: 22, height: 22 }}
      >
        {checked && <Check size={12} className="text-white" strokeWidth={3} />}
      </div>
      <div className="min-w-0">
        <div className="text-[15px] font-semibold text-neutral-900 leading-snug">
          {label}
        </div>
        <div className="text-[13px] text-neutral-500 mt-1 leading-snug">
          {desc}
        </div>
      </div>
    </div>
    {subSelector && (
      <div className="px-5 pb-5 border-t border-neutral-100 pt-4">
        {subSelector}
      </div>
    )}
  </div>
)

interface Props {
  integration?: Integration
}

const WidgetsCheckboxes = ({ integration }: Props) => {
  const { app_settings, saveAppSetting, saveSetting, values } =
    useIntegrationSettingsForm(integration)

  const { settings } = app_settings ?? {}
  const user = useAppSelector(selectUserDetails)
  const ownerId = values.owner || user?.id || ""
  const isCurrentUser = ownerId === user?.id
  const needsOwnerFetch = !integration?.brand_id && !isCurrentUser && !!ownerId

  const { data: ownerData } = useFetchLoanOfficerQuery(ownerId, {
    skip: !needsOwnerFetch
  })
  const { data: ownerOrg } = useFetchOrgDetailsQuery(
    { id: String(ownerData?.org_id ?? "") },
    { skip: !needsOwnerFetch || !ownerData?.org_id }
  )

  const brandId = useMemo(() => {
    if (integration?.brand_id) return integration.brand_id
    if (values.brand_id) return values.brand_id
    const orgToUse = isCurrentUser ? user?.org : ownerOrg
    if (orgToUse && ownerId) {
      const fallbackBrandId = getUserBrandId(orgToUse, ownerId)
      return fallbackBrandId ?? orgToUse?.brands?.[0]?.id
    }
    return undefined
  }, [
    integration?.brand_id,
    values.brand_id,
    isCurrentUser,
    user?.org,
    ownerOrg,
    ownerId
  ])

  const integrationWithBrandId = useMemo(() => {
    const base = integration ?? values
    if (!base) return undefined
    return { ...base, brand_id: brandId ?? base.brand_id ?? "" }
  }, [integration, values, brandId])

  // ── Derived checked states ──────────────────────────────────────────────
  const mortCalc = app_settings?.mort_calc === 1
  const affordCalc = app_settings?.afford_calc === 1
  const leadWorkflow =
    !!app_settings?.lead_workflow && app_settings.lead_workflow >= 1
  const pricing = app_settings?.pricing === 1
  const contactForm = !!(settings?.contactForm && settings.contactForm > 0)
  const rateTable = settings?.rateTable ?? false
  const loReviews = settings?.loReviews ?? false
  const brandReviews = settings?.brandReviews ?? false
  const aboutBoxes = settings?.aboutBoxes ?? false

  // ── Module definitions ──────────────────────────────────────────────────
  const modules: ModuleTileProps[] = [
    // First rows: All modules with dropdowns
    {
      id: "lead_workflow",
      label: "Lead Workflow",
      desc: "A streamlined way to collect key info and convert traffic into qualified leads.",
      checked: leadWorkflow,
      onChange: () => saveAppSetting({ lead_workflow: leadWorkflow ? 0 : 1 }),
      subSelector:
        leadWorkflow && integrationWithBrandId ? (
          <LeadWorkflowSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "rateTable",
      label: "Rate Table",
      desc: "Shows real-time mortgage rates to boost engagement and conversions.",
      checked: rateTable,
      onChange: () => saveSetting({ rateTable: !rateTable }),
      subSelector:
        rateTable && integration ? (
          <RateTableSelector integration={integration} />
        ) : undefined
    },
    {
      id: "pricing",
      label: "Pricing Cards",
      desc: "Real-time rate display based on fixed assumptions.",
      checked: pricing,
      onChange: () => {
        const next = pricing ? 0 : 1
        saveAppSetting({
          pricing: next,
          settings: {
            ...settings,
            rateCards: !!next,
            ...(pricing && { rateCardsConfigId: null })
          }
        })
      },
      subSelector:
        pricing && integrationWithBrandId ? (
          <RateCardsSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "contactForm",
      label: "Contact Form",
      desc: "Captures inquiries and contact details for prospects to connect.",
      checked: contactForm,
      onChange: () => saveSetting({ contactForm: contactForm ? 0 : 1 }),
      subSelector:
        contactForm && integrationWithBrandId ? (
          <ContactFormSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    // Remaining modules without dropdowns
    {
      id: "mort_calc",
      label: "Mortgage Calculator",
      desc: "Quickly estimate monthly payments, affordability, and loan costs.",
      checked: mortCalc,
      onChange: () => saveAppSetting({ mort_calc: mortCalc ? 0 : 1 })
    },
    {
      id: "afford_calc",
      label: "Affordability Calculator",
      desc: "Estimate your affordable home price based on income and expenses.",
      checked: affordCalc,
      onChange: () => saveAppSetting({ afford_calc: affordCalc ? 0 : 1 })
    },
    {
      id: "loReviews",
      label: "LO Reviews",
      desc: "Pulls & displays loan officer specific reviews from leading review providers.",
      checked: loReviews,
      onChange: () => saveSetting({ loReviews: !loReviews })
    },
    {
      id: "brandReviews",
      label: "Brand Reviews",
      desc: "Pulls and displays brand specific reviews from leading review providers.",
      checked: brandReviews,
      onChange: () => saveSetting({ brandReviews: !brandReviews })
    },
    {
      id: "aboutBoxes",
      label: "About Boxes",
      desc: "Your Mission & team bios showcased on your profile pages.",
      checked: aboutBoxes,
      onChange: () => saveSetting({ aboutBoxes: !aboutBoxes })
    }
  ]

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {modules.map(mod => (
        <ModuleTile key={mod.id} {...mod} />
      ))}
    </div>
  )
}

export default WidgetsCheckboxes
